import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { getOperations } from "../services/api-server/operations";
import { getWells } from "../services/api-server/wells";
import {
	combineIdMaps,
	groupObjectIdsByOperator,
	toIdMap,
} from "../utils/reporting/reportingHubFilters";
import useAllProjects from "./useAllProjects";

export const useReportingHubFilterAssets = (
	reports: any[],
	currentTenantKey: string
) => {
	const allProjectsQuery = useAllProjects();

	// Only ids are looked up: a legacy report's text well or operation would
	// fail its tenant's whole request.
	const wellIdsByOperator = useMemo(
		() => groupObjectIdsByOperator(reports, "well"),
		[reports]
	);
	const operationIdsByOperator = useMemo(
		() => groupObjectIdsByOperator(reports, "operation"),
		[reports]
	);

	// Combined, so the maps keep their identity until their records change:
	// without combine, useQueries returns a new array every render.
	const wells = useQueries({
		queries: [...wellIdsByOperator].map(([operator, ids]) => ({
			queryKey: ["reporting-hub-wells", operator, ids.join(",")],
			queryFn: async () => {
				return getWells(operator, {
					_id: { $in: ids },
				}) as Promise<any[] | null>;
			},
			enabled: !!currentTenantKey,
			staleTime: 5 * 60 * 1000,
		})),
		combine: combineIdMaps,
	});

	const operations = useQueries({
		queries: [...operationIdsByOperator].map(([operator, ids]) => ({
			queryKey: ["reporting-hub-operations", operator, ids.join(",")],
			queryFn: async () => {
				return getOperations(operator, {
					_id: { $in: ids },
				}) as Promise<any[] | null>;
			},
			enabled: !!currentTenantKey,
			staleTime: 5 * 60 * 1000,
		})),
		combine: combineIdMaps,
	});

	const projectMap = useMemo(
		() => toIdMap(allProjectsQuery.data, "_id"),
		[allProjectsQuery.data]
	);

	const isLoading =
		allProjectsQuery.isLoading || wells.isLoading || operations.isLoading;

	return {
		projectMap,
		wellMap: wells.map,
		operationMap: operations.map,
		isLoading,
	};
};
